"use client";

import { CommandGroup } from "@universe/ui/components/command";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";

import type { ProductType } from "@/constants";
import { useBalances } from "@/hooks/useBalances";
import useBaseCurrencyConverter from "@/hooks/useBaseCurrencyConverter";
import { useCapabilities } from "@/hooks/useCapabilities";
import useCurrencyFormatter from "@/hooks/useCurrencyFormatter";
import { useCustomerAccount, useGlobalData } from "@/providers";
import { compareCurrencyRank, isUsdStablecoin } from "@/utils/sortProducts";

import { AccountSelectItem } from "./account-select-item";
import { AccountSelectPopover } from "./account-select-popover";

type PayableAccountsSelectProps = {
  className?: string;
  placeholder?: string;
  currentAccountId?: string;
  onSelect?: (accountId: string) => void;
  showFlag?: boolean;
  flagSize?: string;
  popoverAlign?: "start" | "center" | "end";
  overrideBaseCurrency?: string;
  allowZeroBalance?: boolean;
  hideBalance?: boolean;
  filterResourceType?: "currencies" | "digital-assets";
  /** Placeholder for the search input inside the popover */
  searchPlaceholder?: string;
  // Custom trigger for embedding in other components
  customTriggerText?: string;
  customTriggerClassName?: string;
  // Show only accounts with balance
  showOnlyActiveAccounts?: boolean;
  // Beneficiary currency - when set, same-currency payments bypass trading restrictions
  beneficiaryCurrency?: string;
  // Include tradable currencies (for cross-currency payments in pay flow)
  includeTradable?: boolean;
};

type PayableAccountItem = {
  value: string;
  label: string;
  name: string;
  searchIndex: string;
  balance: number;
  type: ProductType;
  disabled: boolean;
  disabledReason?: string;
};

/**
 * Account select component for the pay page.
 * Shows payable products with their balances (or 0 if no balance).
 */
export function PayableAccountsSelect({
  className,
  placeholder,
  currentAccountId,
  onSelect,
  showFlag = false,
  flagSize = "28px",
  popoverAlign = "start",
  overrideBaseCurrency,
  allowZeroBalance = false,
  hideBalance = false,
  filterResourceType,
  searchPlaceholder,
  customTriggerText,
  customTriggerClassName,
  showOnlyActiveAccounts = false,
  beneficiaryCurrency,
  includeTradable = false,
}: PayableAccountsSelectProps) {
  const tAccounts = useTranslations("accounts");
  const [open, setOpen] = useState(false);
  // Use local state only for user selections, prefer currentAccountId from props
  const [localValue, setLocalValue] = useState("");
  // Derive value: prefer prop from parent, fallback to local selection
  const value = currentAccountId || localValue;

  const {
    payable,
    tradable,
    isLoading: isCapabilitiesLoading,
  } = useCapabilities();
  const { balances, isLoading: isBalancesLoading } = useBalances();
  const { products } = useGlobalData();
  const { formatCurrency } = useCurrencyFormatter();
  const { convert } = useBaseCurrencyConverter();
  const { customerAccountBaseCurrency } = useCustomerAccount();

  const isLoading = isCapabilitiesLoading || isBalancesLoading;

  // Build tradable map for quick lookup of trading status
  const tradableMap = useMemo(() => {
    const map = new Map<
      string,
      { isTradable: boolean; tradingRestriction?: string }
    >();
    tradable.forEach((t) => {
      map.set(t.id, {
        isTradable: t.isTradable && !t.tradingRestriction,
        tradingRestriction: t.tradingRestriction,
      });
    });
    return map;
  }, [tradable]);

  // Build list of payable accounts with balances
  const accountsList = useMemo(() => {
    // Optionally combine payable + tradable currencies (tradable currencies can be used for cross-currency payments)
    let combinedProducts = [...payable];

    if (includeTradable) {
      const payableIds = new Set(payable.map((p) => p.id));

      // Add tradable currencies that are not already in payable and have isTradable: true
      const additionalTradable = tradable
        .filter(
          (t) => !payableIds.has(t.id) && t.isTradable && !t.tradingRestriction
        )
        .map((t) => ({ id: t.id, resourceType: t.resourceType }));

      combinedProducts = [...payable, ...additionalTradable];
    }

    // Filter by resourceType if specified
    let filteredProducts = filterResourceType
      ? combinedProducts.filter(
          (product) => product.resourceType === filterResourceType
        )
      : combinedProducts;

    // Filter to only active accounts (those with a balance record) if showOnlyActiveAccounts is true
    if (showOnlyActiveAccounts) {
      filteredProducts = filteredProducts.filter((product) => {
        const balanceInfo = balances[product.id];
        return balanceInfo !== undefined;
      });
    }

    const items: PayableAccountItem[] = filteredProducts.map((product) => {
      const productInfo = products?.find((p) => p.code === product.id);
      const balanceInfo = balances[product.id];
      const balance = balanceInfo?.balance ?? 0;
      const name = productInfo?.name || product.id;

      // Compute trading status
      const tradableInfo = tradableMap.get(product.id);
      const isTradingDisabled = tradableInfo ? !tradableInfo.isTradable : true;
      const requiresTrading =
        beneficiaryCurrency && product.id !== beneficiaryCurrency;
      // A cross-currency payment exchanges this account's currency INTO the
      // beneficiary currency, so both legs must be tradable. If the beneficiary
      // currency itself isn't tradable (e.g. payable-but-not-tradable), no
      // exchange route exists and every cross-currency source must be blocked.
      const beneficiaryNotTradable =
        !!beneficiaryCurrency &&
        !tradableMap.get(beneficiaryCurrency)?.isTradable;
      const isTradingBlocked =
        requiresTrading && (isTradingDisabled || beneficiaryNotTradable);
      const isBalanceBlocked =
        !hideBalance && balance === 0 && !allowZeroBalance;
      const isDisabled = isTradingBlocked || isBalanceBlocked;

      let disabledReason: string | undefined;
      if (isTradingBlocked) {
        disabledReason = tradableInfo?.tradingRestriction
          ? tAccounts(`tradingRestriction.${tradableInfo.tradingRestriction}`)
          : tAccounts("unavailable");
      } else if (isBalanceBlocked) {
        disabledReason = tAccounts("noBalance");
      }

      return {
        value: product.id,
        label: hideBalance
          ? name
          : (formatCurrency(balance, product.id) ?? product.id),
        name,
        type: product.resourceType,
        searchIndex: `${product.id}-${name}`,
        balance,
        disabled: isDisabled,
        disabledReason,
      };
    });

    // Use overrideBaseCurrency if provided, otherwise use customerAccountBaseCurrency
    const baseCurrency = overrideBaseCurrency || customerAccountBaseCurrency;

    // Sort: base currency first, then USD stablecoins (for digital assets), then enabled items with balance, then disabled at bottom, then currency rank
    return items.sort((a, b) => {
      // Base currency always first. GBP no longer gets an unconditional second
      // slot — a currency holding value now outranks a zero-balance GBP.
      if (a.value === baseCurrency) return -1;
      if (b.value === baseCurrency) return 1;

      // For digital assets, USD stablecoins come first
      if (filterResourceType === "digital-assets") {
        const aIsUsd = isUsdStablecoin(a.value);
        const bIsUsd = isUsdStablecoin(b.value);
        if (aIsUsd && !bIsUsd) return -1;
        if (!aIsUsd && bIsUsd) return 1;
      }

      // Disabled items go to the bottom
      if (a.disabled !== b.disabled) {
        return a.disabled ? 1 : -1;
      }

      // Within enabled items: items with balance first
      if (!a.disabled && !b.disabled) {
        const hasBalanceA = a.balance > 0;
        const hasBalanceB = b.balance > 0;
        if (hasBalanceA !== hasBalanceB) return hasBalanceA ? -1 : 1;

        if (hasBalanceA && hasBalanceB) {
          const balanceA = convert(a.balance, a.value);
          const balanceB = convert(b.balance, b.value);
          if (balanceB !== balanceA) return balanceB - balanceA;
        }
      }

      // Fallback: fiat majors, then other fiat, then blockchain
      return compareCurrencyRank(
        { code: a.value, type: a.type },
        { code: b.value, type: b.type }
      );
    });
  }, [
    payable,
    tradable,
    balances,
    products,
    formatCurrency,
    convert,
    overrideBaseCurrency,
    customerAccountBaseCurrency,
    hideBalance,
    filterResourceType,
    showOnlyActiveAccounts,
    beneficiaryCurrency,
    allowZeroBalance,
    tAccounts,
    tradableMap,
    includeTradable,
  ]);

  const selectedAccount = accountsList.find(
    (account) => account.value === value
  );

  const handleSelect = (accountId: string) => {
    // Close popover first to prevent re-render issues during parent state updates
    setOpen(false);
    setLocalValue(accountId);
    onSelect?.(accountId);
  };

  return (
    <AccountSelectPopover
      open={open}
      onOpenChange={setOpen}
      isLoading={isLoading}
      className={className}
      popoverAlign={popoverAlign}
      selectedValue={selectedAccount?.value}
      selectedName={selectedAccount?.name}
      selectedLabel={hideBalance ? undefined : selectedAccount?.label}
      showFlag={showFlag}
      flagSize={flagSize}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      customTriggerText={customTriggerText}
      customTriggerClassName={customTriggerClassName}
    >
      <CommandGroup>
        {accountsList.map((account) => (
          <AccountSelectItem
            key={account.searchIndex}
            value={account.value}
            label={hideBalance ? account.name : account.label}
            name={hideBalance ? account.value : account.name}
            searchIndex={account.searchIndex}
            isSelected={value === account.value}
            onSelect={() => handleSelect(account.value)}
            disabled={account.disabled}
            disabledReason={account.disabledReason}
          />
        ))}
      </CommandGroup>
    </AccountSelectPopover>
  );
}
